import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Mail, Bell, MessageCircle, Smartphone, Save, Eye, History, ChevronRight, X, CheckCircle2,
} from 'lucide-react'
import api from '../api/client'

const inp = 'w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-accent-500'
const card = 'bg-white rounded-2xl border border-neutral-200 shadow-sm'
const CANAIS = [['app', 'Central', Bell], ['push', 'Push', Smartphone], ['whatsapp', 'WhatsApp', MessageCircle], ['email', 'E-mail', Mail]]

export default function NotificacaoTemplates() {
  const [variaveis, setVariaveis] = useState([])
  const [lista, setLista] = useState([])
  const [sel, setSel] = useState(null)
  const [prev, setPrev] = useState(null)
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)
  const textoRef = useRef(null)

  const carregar = useCallback(async () => {
    const { data } = await api.get('/notificacao-templates')
    setVariaveis(data.variaveis || [])
    setLista(data.templates || [])
  }, [])
  useEffect(() => { carregar() }, [carregar])

  function abrir(t) { setSel({ ...t, canais: { app: true, push: true, whatsapp: false, email: false, ...(t.canais || {}) } }); setPrev(null); setMsg('') }
  const set = (k, v) => setSel((s) => ({ ...s, [k]: v }))
  const setCanal = (k, v) => setSel((s) => ({ ...s, canais: { ...s.canais, [k]: v } }))

  function inserirVar(v) {
    const tag = `{{${v}}}`
    const el = textoRef.current
    if (!el) { set('texto', (sel.texto || '') + tag); return }
    const ini = el.selectionStart ?? (sel.texto || '').length
    const fim = el.selectionEnd ?? ini
    const novo = (sel.texto || '').slice(0, ini) + tag + (sel.texto || '').slice(fim)
    set('texto', novo)
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = ini + tag.length }, 0)
  }

  async function salvar() {
    setBusy(true); setMsg('')
    try {
      const { data } = await api.put(`/notificacao-templates/${sel.evento}`, {
        titulo: sel.titulo, texto: sel.texto, ativo: sel.ativo, canais: sel.canais,
      })
      setSel((s) => ({ ...s, historico: data.historico }))
      await carregar()
      setMsg('Template salvo.')
    } catch (e) { setMsg(e?.response?.data?.detail || 'Erro ao salvar.') } finally { setBusy(false) }
  }
  async function previsualizar() {
    const { data } = await api.post(`/notificacao-templates/${sel.evento}/preview`, { titulo: sel.titulo, texto: sel.texto })
    setPrev(data)
  }

  // agrupa por categoria
  const grupos = {}
  for (const t of lista) (grupos[t.categoria || 'outros'] ||= []).push(t)

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="text-accent-500" size={22} />
        <h1 className="text-xl font-heading font-extrabold text-graphite">Templates de Notificação</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">Edite os textos das notificações do sistema por evento — sem precisar de desenvolvimento. Use variáveis como <code className="bg-neutral-100 px-1 rounded">{'{{cliente}}'}</code>.</p>

      {!sel && (
        <div className="space-y-4">
          {Object.entries(grupos).map(([cat, itens]) => (
            <div key={cat} className={card + ' overflow-hidden'}>
              <p className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">{cat}</p>
              <div className="divide-y divide-neutral-100">
                {itens.map((t) => (
                  <button key={t.evento} onClick={() => abrir(t)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50">
                    <div className="flex-1">
                      <p className="font-semibold text-graphite text-sm">{t.nome}</p>
                      <p className="text-xs text-neutral-500 truncate max-w-xl">{t.titulo}</p>
                    </div>
                    <div className="flex gap-1">
                      {CANAIS.filter(([k]) => (t.canais || {})[k]).map(([k, , Ic]) => <Ic key={k} size={13} className="text-neutral-400" />)}
                    </div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${t.ativo ? 'bg-emerald-50 text-emerald-600' : 'bg-neutral-100 text-neutral-400'}`}>{t.ativo ? 'Ativo' : 'Off'}</span>
                    <ChevronRight size={16} className="text-neutral-400" />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {sel && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between">
              <button onClick={() => setSel(null)} className="text-sm text-neutral-500 hover:text-graphite inline-flex items-center gap-1"><X size={15} /> Voltar</button>
              <label className="flex items-center gap-2 text-sm text-neutral-700"><input type="checkbox" checked={!!sel.ativo} onChange={(e) => set('ativo', e.target.checked)} className="w-4 h-4 accent-orange-500" /> Ativo (envia)</label>
            </div>
            {msg && <div className="p-2.5 rounded-xl bg-accent-50 border border-accent-200 text-accent-800 text-sm">{msg}</div>}
            <div className={card + ' p-4 space-y-3'}>
              <p className="font-heading font-bold text-graphite text-sm">{sel.nome} <span className="text-neutral-400 font-normal text-xs">· {sel.evento}</span></p>
              <div><label className="block text-[11px] font-semibold text-neutral-500 uppercase mb-1">Título</label><input className={inp} value={sel.titulo || ''} onChange={(e) => set('titulo', e.target.value)} /></div>
              <div><label className="block text-[11px] font-semibold text-neutral-500 uppercase mb-1">Texto</label>
                <textarea ref={textoRef} className={inp + ' min-h-[120px]'} value={sel.texto || ''} onChange={(e) => set('texto', e.target.value)} /></div>
              <div>
                <p className="text-[11px] text-neutral-500 mb-1">Inserir variável:</p>
                <div className="flex flex-wrap gap-1.5">
                  {variaveis.map((v) => <button key={v} onClick={() => inserirVar(v)} className="text-[11px] font-mono bg-neutral-100 hover:bg-accent-100 text-graphite rounded px-2 py-1">{`{{${v}}}`}</button>)}
                </div>
              </div>
              <div>
                <p className="text-[11px] font-semibold text-neutral-500 uppercase mb-1">Canais</p>
                <div className="flex flex-wrap gap-2">
                  {CANAIS.map(([k, label, Ic]) => (
                    <button key={k} onClick={() => setCanal(k, !sel.canais[k])} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${sel.canais[k] ? 'bg-accent-500 text-white border-accent-500' : 'bg-white text-neutral-500 border-neutral-200'}`}>
                      <Ic size={13} /> {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={salvar} disabled={busy} className="inline-flex items-center gap-1.5 rounded-xl text-white font-bold px-4 py-2 text-sm disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#ffcc00,#ff6c00)' }}><Save size={15} /> {busy ? 'Salvando...' : 'Salvar'}</button>
                <button onClick={previsualizar} className="inline-flex items-center gap-1.5 rounded-xl border border-neutral-200 text-graphite font-semibold px-4 py-2 text-sm hover:bg-neutral-50"><Eye size={15} /> Pré-visualizar</button>
              </div>
            </div>

            {(sel.historico || []).length > 0 && (
              <div className={card + ' p-4'}>
                <p className="font-heading font-bold text-graphite text-sm flex items-center gap-1.5 mb-2"><History size={15} className="text-accent-500" /> Histórico</p>
                <div className="space-y-1 max-h-48 overflow-y-auto text-xs">
                  {[...sel.historico].reverse().map((h, i) => (
                    <div key={i} className="border-b border-neutral-100 py-1 text-neutral-600">
                      <span className="text-neutral-400">{(h.data || '').slice(0, 16).replace('T', ' ')}</span> · <b>{h.usuario}</b>
                      <div className="text-neutral-500 line-through">{h.texto_anterior}</div>
                      <div className="text-emerald-700">{h.texto_novo}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pré-visualização */}
          <div className="space-y-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Pré-visualização</p>
            {!prev && <p className="text-sm text-neutral-400">Clique em "Pré-visualizar" para ver como fica em cada canal (com dados de exemplo).</p>}
            {prev && (
              <>
                <PreviewBox icon={Bell} titulo="Central / App"><b>{prev.central.titulo}</b><p>{prev.central.texto}</p></PreviewBox>
                <PreviewBox icon={Smartphone} titulo="Push"><b>{prev.push.titulo}</b><p>{prev.push.texto}</p></PreviewBox>
                <PreviewBox icon={MessageCircle} titulo="WhatsApp"><p className="whitespace-pre-line">{prev.whatsapp.texto}</p></PreviewBox>
                <PreviewBox icon={Mail} titulo="E-mail"><b>{prev.email.assunto}</b><p>{prev.email.corpo}</p></PreviewBox>
                <p className="text-[10px] text-neutral-400 flex items-center gap-1"><CheckCircle2 size={11} /> Exemplo: cliente {prev.contexto_exemplo?.cliente}, valor {prev.contexto_exemplo?.valor}</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function PreviewBox({ icon: Ic, titulo, children }) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-3 text-sm text-neutral-700">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-neutral-400 mb-1"><Ic size={12} /> {titulo}</div>
      {children}
    </div>
  )
}
